import React from "react";
import { Calculation, Selection } from "../../../../src/application/Contracts";
import APIServicesRegistry from "../../../api-services/APIServicesRegistry";
import TWSCalculationApiService from "../../../api-services/TWSCalculationApiService";
import flattenConfigurationModel from "../../../flattenConfigurationModel";
import Field from "../../../model/configurator/Field";
import StepModel from "../../../model/configurator/StepModel";
import formModelStore from "../../../store/FormModelStore";
import ExpandableContainer from "../../configuration/steps/ExpandableContainer";
import BaseStep from "../../shared/steps/BaseStep";
import TwsMatchingTankQuantityField from "./custom-fields/tws-matching-tank-quantity/TwsMatchingTankQuantityField";
import TwsTankSelectionResultField from "./custom-fields/tws-tank-selection-result/TwsTankSelectionResultField";
import TwsMatchingUnitField from "./custom-fields/tws-matching-units/TwsMatchingUnitField";
import { TankSelectionTechnicalData } from "./tank-selection/TankSelectionTechnicalData";
import { WaterSystemTechnicalData } from "./water-system/WaterSystemTechnicalData";
import { RenderStepInputReadonlyField } from "./custom-fields/Helper";
import { TwsProductCatalogSelectProduct } from "./custom-fields/tws-product-catalog-select-product/TwsProductCatalogSelectProduct";
import { GetOptionsWeight, GetTankWeight, IsInstantaneousWasSelectedOnPreviousStep, IsPrimaryTankWasSelected, TankOnSite } from "../../utils/FormModelStoreTWSHelper";
import MatchingUnit = Calculation.MatchingUnitModel;
import WaterSystemTechnicalDataModel = Calculation.WaterSystemTechnicalDataModel;
import InputMethodValues = Selection.InputMethodValuesModel;
import InputMethod = Calculation.InputMethod;
import TankResultModel = Selection.TankResultModel;
import TankTechnicalData = Selection.TankTechnicalDataModel;
import MatchingTankQuantitiesSelectionResult = Calculation.MatchingTankQuantitiesSelectionResult;
import TankType = Selection.TankType;
import getTankMaterialsName = Selection.getTankMaterialsName;
import TankMaterials = Selection.TankMaterials;
import getInspectionHoleName = Selection.getInspectionHoleName;
import InspectionHole = Selection.InspectionHole;
import MatchingQuantityModel = Selection.MatchingQuantityModel;
import TankFamily = Selection.TankFamily;
import { upperFirst } from "lodash";
import getTankTypeName = Selection.getTankTypeName;
import WaterSystemOptionModel = Calculation.WaterSystemOptionModel;
import { StepsSlugs } from "./TWSProjectCalculationContainer";

interface SelfState {
    matchingUnits: MatchingUnit[];
    capacityCalculation?: any;
    productCatalogCalculation?: any;
    matchingWaterSystemTechnicalData: WaterSystemTechnicalDataModel | null;
    currentTankTypeSlug?: string;
    matchingQuantitiesResultModel?: MatchingTankQuantitiesSelectionResult;
    tankResults: TankResultModel[];
    technicalData: TankTechnicalData | null;
    inputMethodValues: InputMethodValues | null;
    selectedTankMatchingUnit?: MatchingQuantityModel;
    editModeLoaded: boolean;
    availableTankMaterials: TankMaterials[];
    availableTankVolumes: number[];
    selectedOptions: WaterSystemOptionModel[];
}

export class TankSelectionStep extends BaseStep {
    _tWSCalculationApiService: TWSCalculationApiService | null = null;
    state: SelfState = {
        matchingUnits: [],
        capacityCalculation: null,
        productCatalogCalculation: null,
        matchingWaterSystemTechnicalData: null,
        tankResults: [],
        technicalData: null,
        inputMethodValues: null,
        editModeLoaded: false,
        availableTankMaterials: [],
        availableTankVolumes: [],
        selectedOptions: []
    }

    constructor(props: any) {
        super(props);

        window.scheduleAtDocumentReady(() => {
            this._tWSCalculationApiService = APIServicesRegistry.getService(TWSCalculationApiService);
        })
    }

    protected override onEditMode(): void {
        if (this.state.editModeLoaded)
            return;

        super.onEditMode();

        this.setState({
            matchingUnits: this.props.model.inputMethod.results?.value.matchingUnitModels,
            capacityCalculation: this.props.model.inputMethod.capacityCalculation,
            productCatalogCalculation: this.props.model.inputMethod.productCatalogCalculation,
            matchingWaterSystemTechnicalData: this.props.model.waterSystem.results?.value.matchingWaterSystemTechnicalDataModel,
            matchingQuantitiesResultModel: this.props.model.tankSelection.results?.value.matchingQuantitiesResultModel,
            tankResults: this.props.model.tankSelection.results?.value.tankResults,
            technicalData: this.props.model.tankSelection.results?.value.tankTechnicalData,
            editModeLoaded: true //Tank selection step is in edit mode...
        });

        window.scheduleAtDocumentReady(() => {
            this.props.stepChangeCallback(this.props.stepSlug, formModelStore.formModel.tankSelection.selectedTank?.value?.ref || TankOnSite());
        });
    }

    static getFields(step: StepModel) {
        const createField = (name: string, required: boolean = false) => new Field(name, step, true, formModelStore, required)
        return {
            mtuTankVolume: createField("matchingTankUnit.tankVolume"),
            mtuTankMaterial: createField("matchingTankUnit.tankMaterial"),
            mtuTankQuantity: createField("matchingTankUnit.tankQuantity"),
            mtqMatchingTankQuantity: new TwsMatchingTankQuantityField("selectedMatchingTankQuantity", step, true, formModelStore),
            mtsTankInsulation: createField("matchingTankSelection.tankInsulation"),
            mtsInspectionHole: createField("matchingTankSelection.inspectionHole"),
            mtsTankOnSite: createField("matchingTankSelection.tankOnSite"),
            tsrTankSelectionResult: new TwsTankSelectionResultField("selectedTank", step, true, formModelStore),
            matchingUnit: new TwsMatchingUnitField("installationTypeCalculation.selectedUnitModel", step, true, formModelStore),
            tankFamily: createField("matchingTankUnit.tankFamily")
        }
    }

    static getStepModel(model: any) {
        return new StepModel('tankSelection', model.tankSelection)
    }

    public getAvailableMatchingTankQuantities = () => {
        this._tWSCalculationApiService?.getAvailableMatchingTankQuantities(flattenConfigurationModel(formModelStore).formModel)
            .then(this.setAvailableMatchingTankQuantities)
    }

    getAvailableTankSelectionResults = () => {
        const model = flattenConfigurationModel(formModelStore).formModel;

        this._tWSCalculationApiService?.getAvailableTankMaterials(model).then(this.setAvailableTankMaterials).then(() => {
            const enabledInspectionHoleItems = this.getInspectionHoleItems().filter(x => !x.disabled).map(x => x.value);
            const enabledMaterials = this.getTankMaterialItems().filter(x => !x.disabled).map(x => x.value);

            if (!enabledInspectionHoleItems.includes(model.tankSelection.matchingTankSelection.inspectionHole))
                model.tankSelection.matchingTankSelection.inspectionHole = enabledInspectionHoleItems[0];

            if (enabledMaterials.includes(model.tankSelection.matchingTankUnit.tankMaterial))
                model.tankSelection.selectedMatchingTankQuantity.material = model.tankSelection.matchingTankUnit.tankMaterial;
            else
                model.tankSelection.selectedMatchingTankQuantity.material = enabledMaterials[0];

            this._tWSCalculationApiService?.getAvailableTankSelectionResults(model).then(this.setAvailableTankSelectionResults);
        });
    }

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{}>, snapshot?: any) {
        if (!prevProps.visible && this.props.visible && !formModelStore.formModel.tankSelection.selectedTank?.value?.ref) {
            if (formModelStore.recentlyChangedFormSection != this.props.stepSlug) {
                this.clearTankData();
            }
            this.getAvailableMatchingTankQuantities();
            this.setState({
                matchingUnits: formModelStore.formModel.inputMethod?.results?.value.matchingUnitModels,
                capacityCalculation: formModelStore.formModel.inputMethod?.capacityCalculation,
                productCatalogCalculation: formModelStore.formModel.inputMethod?.productCatalogCalculation,
                matchingWaterSystemTechnicalData: formModelStore.formModel.waterSystem?.results?.value.matchingWaterSystemTechnicalDataModel,
                selectedOptions: prevProps.model.waterSystem?.selectedTWSOptions?.value
            });

            this.updateInputsBasedOnSelectedFamily().then(this.getAvailableMatchingTankQuantities);
            this.props.stepChangeCallback(this.props.stepSlug, formModelStore.formModel.tankSelection.selectedTank?.value?.ref || TankOnSite());
            this.props.setLastAvailableStep(this.props.stepSlug);
        }

        if (formModelStore.recentlyChangedFormSection === StepsSlugs.WaterSystem) {
            this.fields.tsrTankSelectionResult.ref.clear();
        }
    }

    updateInputsBasedOnSelectedFamily = async (): Promise<void> => {
        const model = flattenConfigurationModel(formModelStore).formModel;

        await this._tWSCalculationApiService?.getAvailableTankMaterials(model).then(this.setAvailableTankMaterials);
        await this._tWSCalculationApiService?.getAvailableTankVolumesForTankSelectionStep(model.tankSelection.matchingTankUnit)
            .then(this.setAvailableTankVolumes);

        if (this.isPrimaryTank()) {
            this.fields.mtsInspectionHole.setValue(InspectionHole.NO);
        } else if (this.fields.mtsInspectionHole.value === InspectionHole.NO) {
            this.fields.mtsInspectionHole.setValue(InspectionHole.HOLE);
        }

        this.forceUpdate();
    }

    isPrimaryTank = (): boolean => {
        return IsPrimaryTankWasSelected(this.state.matchingQuantitiesResultModel?.tankType);
    }

    render() {
        return (<React.Fragment>
            <div className={`generic-step-content`}>
                <div className={`row row-no-gutter`}>
                    <div className="column-6 column-xs-12">
                        <div className={`card`}>
                            <div className={`card-body`}>
                                {this.renderMatchingTankUnits()}
                                {this.renderMatchingTankQuantities()}
                                {this.renderTankSelectionResults()}
                            </div>
                        </div>
                    </div>
                    <div className="column-6 column-xs-12">
                        <div className={`card`}>
                            <div className={`card-body`}>
                                {this.renderGeneralInfo()}
                            </div>
                        </div>
                        <br />
                        {!this.tankSelectionOnly() && (<>
                            <div className={`card`}>
                                <div className={`card-body`}>
                                    {this.renderTwsTechnicalData()}
                                </div>
                            </div>
                            <br /></>)}
                        {!TankOnSite() &&
                            <div className={`card`}>
                                <div className={`card-body`}>
                                    {this.renderTankTechnicalData()}
                                </div>
                            </div>}
                    </div>
                </div>
            </div>
        </React.Fragment>)
    }

    getTankMaterialItems = () => {
        let items = [TankMaterials.Stainless, TankMaterials.Enamelled, TankMaterials.CarbonSteel];
        return items.map(i => {
            return {
                value: i,
                text: __(getTankMaterialsName(i)),
                disabled: !this.state.availableTankMaterials.includes(i)
            };
        })
    }

    getTankVolumeItems = () => {
        return this.state.availableTankVolumes.map(i => ({ text: i, value: i }));
    }

    tankSelectionOnly = (): boolean => {
        return formModelStore.formModel.inputMethod?.inputMethod.value === InputMethod.ProductCatalog && formModelStore.formModel.inputMethod?.productCatalogCalculation?.selectedProduct?.value?.name === upperFirst(TankFamily.AquaTank);
    }

    getTankFamilyItems = () => {
        const items = this.fields.tankFamily.items;

        if (this.tankSelectionOnly()) {
            items.find((x: any) => x.value === upperFirst(Selection.AquaTankType.NO)).disabled = true;

            if (this.fields.tankFamily.value === upperFirst(Selection.AquaTankType.NO))
                this.fields.tankFamily.value = items.find((x: any) => x.value !== upperFirst(Selection.AquaTankType.NO)).value;

            return items;
        }

        return items;
    }

    renderMatchingTankUnits(): any {
        return <React.Fragment>
            <ExpandableContainer arrowFirst={true} label={__('Matching Tank Units')} initialOpen={true}>
                <div className={`step-inputs-row`}>
                    {formModelStore.formModel.inputMethod?.inputMethod.value === InputMethod.ProductCatalog &&
                        <>
                            {this.renderField(this.fields.tankFamily, { items: this.getTankFamilyItems(), readonly: !this.tankSelectionOnly() })}
                            {this.renderField(this.fields.mtuTankVolume, {
                                items: this.getTankVolumeItems()
                            })}
                        </>}
                    {this.renderField(this.fields.mtuTankMaterial, {
                        disabled: IsInstantaneousWasSelectedOnPreviousStep(),
                        items: this.getTankMaterialItems()
                    })}
                    {this.renderField(this.fields.mtuTankQuantity)}
                </div>
                {RenderStepInputReadonlyField("Tank type", __(getTankTypeName(this.state.matchingQuantitiesResultModel?.tankType ?? TankType.NoTank)))}
            </ExpandableContainer>
        </React.Fragment>
    }

    hasSelectedMatchingQuantity = () => {
        return formModelStore.formModel?.tankSelection?.selectedMatchingTankQuantity?.value?.ref > 0;
    }

    renderMatchingTankQuantities(): any {
        return <React.Fragment>
            {
                <ExpandableContainer arrowFirst={true} label={__('Matching Tank Quantities')} initialOpen={true} additionalClasses={['zero-padding']}>
                    {this.renderField(this.fields.mtqMatchingTankQuantity, { additionalModel: this.state.matchingQuantitiesResultModel?.matchingQuantityModels ?? [], value: this.state.selectedTankMatchingUnit })}
                    {this.hasSelectedMatchingQuantity() ?
                        <>
                            <div className={'line-separator'} />
                            <div className={`step-inputs-row`}>
                                {RenderStepInputReadonlyField("Tank quantity", formModelStore.formModel?.tankSelection?.selectedMatchingTankQuantity?.value?.madeOfNX)}
                                {RenderStepInputReadonlyField("Unitary volume", formModelStore.formModel?.tankSelection?.selectedMatchingTankQuantity?.value?.unitVolL, "l")}
                            </div>
                        </> : <></>}
                </ExpandableContainer>}
        </React.Fragment>
    }

    getInspectionHoleItems = () => {
        return [InspectionHole.NO, InspectionHole.HOLE, InspectionHole.MANHOLE].map(i => {
            return {
                value: i,
                text: __(getInspectionHoleName(i)),
                disabled: i === InspectionHole.NO && !IsInstantaneousWasSelectedOnPreviousStep() && !this.isPrimaryTank() || i !== InspectionHole.NO && this.isPrimaryTank()
            };
        })
    }

    renderTankSelectionResults() {
        const selectedMatchingTankQuantity = formModelStore.formModel.tankSelection.selectedMatchingTankQuantity.value;

        return <React.Fragment>
            {
                <ExpandableContainer arrowFirst={true} label={__('Tank Selection Results')} initialOpen={true} additionalClasses={['zero-padding']}>
                    {!!selectedMatchingTankQuantity &&
                        <div className={`step-inputs-row`}>
                            {this.renderField(this.fields.mtsTankOnSite)}
                        </div>
                    }
                    {(!TankOnSite() || !selectedMatchingTankQuantity) &&
                        <>
                            <div className={`step-inputs-row`}>
                                {this.renderField(this.fields.mtsTankInsulation, { items: this.getInsulationTranslatedValues() })}
                                {this.renderField(this.fields.mtsInspectionHole, { items: this.getInspectionHoleItems(), disabled: this.isPrimaryTank() })}
                            </div>
                            {this.renderField(this.fields.tsrTankSelectionResult, { additionalModel: this.state.tankResults })}
                        </>
                    }
                </ExpandableContainer>}
        </React.Fragment>
    }

    getInsulationTranslatedValues() {
        return this.fields.mtsTankInsulation.items.map((i: any) => {
            return {
                attributes: i.attributes,
                disabled: i.disabled,
                value: i.value,
                text: __(i.text)
            }
        });
    }
    renderGeneralInfo() {
        return <React.Fragment>
            <ExpandableContainer arrowFirst={true} label={__('General info')} initialOpen={false} additionalClasses={[formModelStore.formModel.inputMethod?.inputMethod.value === InputMethod.InstallationType ? 'zero-padding' : '']}>
                {this.renderInstallationTypeCalculationGeneralInfo()}
                {this.renderCapacityCalculationGeneralInfo()}
                {this.renderProductCatalogCalculationGeneralInfo()}
            </ExpandableContainer>
        </React.Fragment>
    }

    renderTwsTechnicalData() {
        return <React.Fragment>
            <ExpandableContainer arrowFirst={true} label={__('TWS technical data')} initialOpen={false}>
                {this.renderWaterSystemTechnicalData()}
            </ExpandableContainer>
        </React.Fragment>
    }

    renderInstallationTypeCalculationGeneralInfo() {
        if (formModelStore.formModel.inputMethod?.inputMethod.value !== InputMethod.InstallationType) {
            return <div></div>
        }
        return this.renderField(this.fields.matchingUnit, {
            additionalModel: this.state.matchingUnits,
            readonly: true
        });
    }

    renderCapacityCalculationGeneralInfo() {
        if (formModelStore.formModel.inputMethod?.inputMethod.value !== InputMethod.Capacity) {
            return <div></div>
        }
        return <div className="step-inputs-row grid-2">
            {RenderStepInputReadonlyField("Capacity", this.state.capacityCalculation?.capacity?.value, "kW")}
            {RenderStepInputReadonlyField("Volume", this.state.capacityCalculation?.volume?.value, "L")}
            {RenderStepInputReadonlyField("Primary tank", this.state.capacityCalculation?.primaryTank?.value ? __("Yes") : __("No"))}
            {RenderStepInputReadonlyField("Secondary tank", this.state.capacityCalculation?.secondaryTank?.value ? __("Yes") : __("No"))}
        </div>
    }

    renderProductCatalogCalculationGeneralInfo = () => {
        if (formModelStore.formModel.inputMethod?.inputMethod.value !== InputMethod.ProductCatalog) {
            return <div></div>
        }
        return <div className="step-inputs-row" style={{ height: 200 }}>
            <div className={`tws-product-family`}>
                <div className={`description`}>
                    <div className={`name`}>
                        {this.state.productCatalogCalculation?.selectedProduct?.value?.name}
                        <div className={`title`}>
                            {__(this.state.productCatalogCalculation?.selectedProduct?.value?.title)}
                        </div>
                    </div>
                    {TwsProductCatalogSelectProduct.renderInformationSection(this.state.productCatalogCalculation?.selectedProduct?.value)}
                </div>
                <div className={`image`} style={{ backgroundImage: `url(${this.state.productCatalogCalculation?.selectedProduct?.value?.image}` }} />
            </div>
        </div>
    }

    renderWaterSystemTechnicalData(): any {
        return <React.Fragment>
            {this.state.matchingWaterSystemTechnicalData?.productFamily && <WaterSystemTechnicalData {...this.state.matchingWaterSystemTechnicalData} tankWeight={GetTankWeight()} optionsWeight={GetOptionsWeight(this.state.selectedOptions)} />}
        </React.Fragment>
    }

    renderTankTechnicalData(): any {
        return <React.Fragment>
            <ExpandableContainer arrowFirst={true} label={__('Tank technical data')} initialOpen={true}>
                {this.state.technicalData != null && <TankSelectionTechnicalData {...this.state.technicalData} />}
            </ExpandableContainer>
        </React.Fragment>
    }

    loadTankTechnicalData = () => {
        this._tWSCalculationApiService?.getTechnicalDataOfTheTank(flattenConfigurationModel(formModelStore).formModel).then((response) => {
            this.setTechnicalDataOfTheTank(response)
            this.props.stepChangeCallback(this.props.stepSlug, true);
            this.props.setDirty()
        });
    }

    wasFieldRecentlyChanged(...fields: Field[]) {
        return fields.some(x => formModelStore.recentlyChangedField === x.getModelPath());
    }

    onFormModelChange() {
        formModelStore.recentlyChangedFormSection = this.props.stepSlug;
        this.cancelSelectionsFromModelStore();
        this.props.setLastAvailableStep(this.props.stepSlug);
        if (this.wasFieldRecentlyChanged(this.fields.mtqMatchingTankQuantity, this.fields.mtsTankInsulation, this.fields.mtsInspectionHole, this.fields.mtsTankOnSite)) {
            formModelStore.formModel.tankSelection.selectedTank.value = null;
            this.setState({ technicalData: null, tankResults: [] });
            this.getAvailableTankSelectionResults();
        } else if (this.wasFieldRecentlyChanged(this.fields.tsrTankSelectionResult)) {
            this.setState({ technicalData: null });
            this.loadTankTechnicalData();
            formModelStore.setDirty(false);
        } else {
            this.clearTankData();
            this.updateInputsBasedOnSelectedFamily().then(this.getAvailableMatchingTankQuantities);
        }

        if (!formModelStore.formModel.tankSelection.selectedMatchingTankQuantity.value && TankOnSite()) {
            this.fields.mtsTankOnSite.setValue(false);
        }

        if (TankOnSite()) {
            this.props.stepChangeCallback(this.props.stepSlug, true);
            formModelStore.formModel.tankSelection.selectedTank.value = null;
            formModelStore.formModel.tankSelection.results.value.tankTechnicalData = null;
            this.setState({ technicalData: null });
        }
    }

    clearTankData = () => {
        formModelStore.formModel.tankSelection.selectedMatchingTankQuantity.value = null;
        formModelStore.formModel.tankSelection.selectedTank.value = null;
        this.setState({ technicalData: null, matchingQuantitiesResultModel: null, tankResults: [] });
    }

    cancelSelectionsFromModelStore = () => {
        this.props.stepChangeCallback(this.props.stepSlug, false);
    }

    setAvailableMatchingTankQuantities = (response: any) => {
        let model = response.data as MatchingTankQuantitiesSelectionResult;
        this.setState({ matchingQuantitiesResultModel: model });
        if (model.matchingQuantityModels.length === 1) {
            formModelStore.formModel.tankSelection.selectedMatchingTankQuantity.value = model.matchingQuantityModels[0];
            this.setState({ selectedTankMatchingUnit: model.matchingQuantityModels[0] })
            formModelStore.recentlyChangedField = this.fields.mtqMatchingTankQuantity.getModelPath();
            this.onFormModelChange();
            this.props.setDirty()
        }

        this.updateInputsBasedOnSelectedFamily();
    }

    setAvailableTankSelectionResults = (response: any) => {
        this.setState({ tankResults: response.data });
        this.props.setDirty();
    }

    setAvailableTankMaterials = (response: any) => {
        this.setState({ availableTankMaterials: response.data });
        this.props.setDirty();

        if (!this.state.availableTankMaterials.includes(this.fields.mtuTankMaterial.value)) {
            this.fields.mtuTankMaterial.setValue(this.state.availableTankMaterials[0]);
        }
    }

    setAvailableTankVolumes = (response: any) => {
        this.setState({ availableTankVolumes: response.data });
        this.props.setDirty();

        if (!!this.state.availableTankVolumes.length && !this.state.availableTankVolumes.includes(this.fields.mtuTankVolume.value)) {
            this.fields.mtuTankVolume.setValue(this.state.availableTankVolumes[0]);
        }
    }

    setTechnicalDataOfTheTank = (response: any) => {
        this.setState({ technicalData: response.data });
        formModelStore.formModel.tankSelection.results.value = {
            tankTechnicalData: response.data,
            tankResults: this.state.tankResults,
            matchingQuantitiesResultModel: this.state.matchingQuantitiesResultModel
        };
    }
}